import { Stack } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'
import { useDrawerSubForm } from 'hooks'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  SaveButton,
  UseDrawerStateReturn,
} from 'ui'
import {
  RegularBenefit,
  RegularBenefitVariant,
} from '../../../../../../typings'
import { BenefitSelector } from '../../../../../BenefitSelector'
import { useGenericComponent } from '../../hooks/useGenericComponent'
import { BENEFIT_OPTIONS } from '../constants'
import { EDITOR_MAP } from './constants'
import { messages } from './messages'
import { benefitValidation } from './validation'

type RegularBenefitEditorProps = {
  state: UseDrawerStateReturn
  config: RegularBenefit
  onSave: (config: RegularBenefit) => void
  onEdit: (key: RegularBenefitVariant) => void
}

export function RegularBenefitEditor(props: RegularBenefitEditorProps) {
  const { state, config, onSave, onEdit } = props

  const intl = useIntl()

  const { drawerForm } = useDrawerSubForm<RegularBenefit>({
    config,
    isOpen: state.open,
    validationFn: benefitValidation(intl),
  })

  const type: RegularBenefitVariant | undefined = useWatch({
    form: drawerForm,
    name: 'type',
  })

  const { GenericComponent } = useGenericComponent({
    settings: EDITOR_MAP,
    type,
  })

  const handleSave = async () => {
    const valid = await drawerForm.trigger()
    if (!valid) return

    onSave(drawerForm.getValues())

    state.hide()
  }

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>
          <FormattedMessage {...messages.title} />
        </DrawerTitle>
        <DismissButton onHide={state.hide} />
      </DrawerHeader>
      <DrawerContent>
        <Stack space="$space-8" fluid>
          <BenefitSelector
            value={type}
            onChange={onEdit}
            options={BENEFIT_OPTIONS}
          />
          <GenericComponent state={drawerForm} />
        </Stack>
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={state.hide} />
        <SaveButton onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
